import { t } from "i18next";
import { useMemo } from "react";
import { addMinutes, addHours } from "date-fns";
import moment from "moment";

export const timezone_data = {
    "UTC−12:00": { value: "-1200", countries: ["Baker Island (US)"] },
    "UTC−11:00": {
        value: "-1100",
        countries: [
            "American Samoa",
            "Niue",
            "Samoa",
            "Jarvis Island",
            "Midway Atoll",
            "Palmyra Atoll",
        ],
    },
    "UTC−10:00": {
        value: "-1000",
        countries: [
            "Hawaii (USA)",
            "French Polynesia (Tahiti)",
            "Cook Islands",
            "Johnston Atoll",
        ],
    },
    "UTC−09:30": {
        value: "-0930",
        countries: ["Marquesas Islands (French Polynesia)"],
    },
    "UTC−09:00": {
        value: "-0900",
        countries: ["Alaska (USA)", "Gambier Islands (French Polynesia)"],
    },
    "UTC−08:00": {
        value: "-0800",
        countries: [
            "USA (Pacific Time)",
            "Canada (Pacific Time)",
            "Pitcairn Islands",
        ],
    },
    "UTC−07:00": {
        value: "-0700",
        countries: [
            "USA (Mountain Time)",
            "Canada (Mountain Time)",
            "Mexico (Chihuahua)",
        ],
    },
    "UTC−06:00": {
        value: "-0600",
        countries: [
            "USA (Central Time)",
            "Canada (Central Time)",
            "Mexico (Central Time)",
            "Guatemala",
            "Ecuador (Galápagos)",
        ],
    },
    "UTC−05:00": {
        value: "-0500",
        countries: [
            "USA (Eastern Time)",
            "Canada (Eastern Time)",
            "Colombia",
            "Peru",
            "Ecuador (mainland)",
            "Panama",
            "Jamaica",
        ],
    },
    "UTC−04:00": {
        value: "-0400",
        countries: [
            "Atlantic Time (Canada)",
            "Bolivia",
            "Venezuela",
            "Paraguay",
            "Barbados",
            "Dominican Republic",
        ],
    },
    "UTC−03:30": { value: "-0330", countries: ["Newfoundland (Canada)"] },
    "UTC−03:00": {
        value: "-0300",
        countries: [
            "Argentina",
            "Brazil (eastern regions)",
            "Uruguay",
            "Suriname",
            "French Guiana",
        ],
    },
    "UTC−02:00": {
        value: "-0200",
        countries: ["South Georgia and the South Sandwich Islands"],
    },
    "UTC−01:00": {
        value: "-0100",
        countries: ["Azores (Portugal)", "Cape Verde"],
    },
    "UTC±00:00": {
        value: "0000",
        countries: [
            "UK",
            "Portugal (mainland)",
            "Iceland",
            "Morocco",
            "Gambia",
            "Ghana",
            "Senegal",
        ],
    },
    "UTC+01:00": {
        value: "0100",
        countries: [
            "France",
            "Germany",
            "Spain",
            "Algeria",
            "Nigeria",
            "Tunisia",
            "Italy",
            "Poland",
            "Norway",
            "Sweden",
        ],
    },
    "UTC+02:00": {
        value: "0200",
        countries: [
            "Greece",
            "Egypt",
            "South Africa",
            "Palestine",
            "Finland",
            "Ukraine",
            "Romania",
            "Bulgaria",
            "Turkey",
        ],
    },
    "UTC+03:00": {
        value: "0300",
        countries: [
            "Kenya",
            "Russia (Moscow)",
            "Saudi Arabia",
            "Iraq",
            "Ethiopia",
            "Qatar",
            "Bahrain",
            "Kuwait",
        ],
    },
    "UTC+03:30": { value: "0330", countries: ["Iran"] },
    "UTC+04:00": {
        value: "0400",
        countries: [
            "Azerbaijan",
            "United Arab Emirates",
            "Mauritius",
            "Oman",
            "Georgia",
            "Armenia",
        ],
    },
    "UTC+04:30": { value: "0430", countries: ["Afghanistan"] },
    "UTC+05:00": {
        value: "0500",
        countries: [
            "Pakistan",
            "Uzbekistan",
            "Turkmenistan",
            "Maldives",
            "Tajikistan",
        ],
    },
    "UTC+05:30": { value: "0530", countries: ["India", "Sri Lanka"] },
    "UTC+05:45": { value: "0545", countries: ["Nepal"] },
    "UTC+06:00": {
        value: "0600",
        countries: [
            "Bangladesh",
            "Bhutan",
            "Kazakhstan (eastern regions)",
            "Kyrgyzstan",
        ],
    },
    "UTC+06:30": { value: "0630", countries: ["Myanmar", "Cocos Islands"] },
    "UTC+07:00": {
        value: "0700",
        countries: [
            "Thailand",
            "Vietnam",
            "Cambodia",
            "Indonesia (western regions)",
            "Laos",
        ],
    },
    "UTC+08:00": {
        value: "0800",
        countries: [
            "China",
            "Singapore",
            "Malaysia",
            "Australia (Western)",
            "Philippines",
            "Brunei",
        ],
    },
    "UTC+08:45": { value: "0845", countries: ["Australia (Eucla)"] },
    "UTC+09:00": {
        value: "0900",
        countries: [
            "Japan",
            "South Korea",
            "Indonesia (eastern regions)",
            "Palau",
        ],
    },
    "UTC+09:30": {
        value: "0930",
        countries: ["Australia (Northern Territory, South Australia)"],
    },
    "UTC+10:00": {
        value: "1000",
        countries: [
            "Australia (Queensland, New South Wales, Victoria)",
            "Papua New Guinea",
            "Guam",
            "Russia (Vladivostok)",
        ],
    },
    "UTC+10:30": { value: "1030", countries: ["Lord Howe Island (Australia)"] },
    "UTC+11:00": {
        value: "1100",
        countries: [
            "Solomon Islands",
            "Vanuatu",
            "New Caledonia",
            "Russia (Magadan)",
        ],
    },
    "UTC+12:00": {
        value: "1200",
        countries: [
            "Fiji",
            "New Zealand",
            "Marshall Islands",
            "Tuvalu",
            "Nauru",
        ],
    },
    "UTC+12:45": {
        value: "1245",
        countries: ["Chatham Islands (New Zealand)"],
    },
    "UTC+13:00": { value: "1300", countries: ["Samoa", "Tonga", "Tokelau"] },
    "UTC+14:00": { value: "1400", countries: ["Line Islands (Kiribati)"] },
};
export function offsetToHours(offset: string): number {
    const sign = offset.startsWith("-") ? -1 : 1;
    const absOffset = offset.replace("-", "").replace("+", "");

    const hours = parseInt(absOffset.slice(0, 2), 10);
    const minutes = parseInt(absOffset.slice(2), 10);

    return sign * (hours + minutes / 60);
}
export function dateDiff(date1: any, date2: any): any {
    var diff: any = {};
    var tmp = date2 - date1;

    tmp = Math.floor(tmp / 1000);
    diff.sec = tmp % 60;

    tmp = Math.floor((tmp - diff.sec) / 60);
    diff.min = tmp % 60;
    tmp = Math.floor((tmp - diff.min) / 60);
    diff.hour = tmp % 24;

    tmp = Math.floor((tmp - diff.hour) / 24);
    diff.day = tmp;

    return diff;
}
export const translatePattern = (patternValue: any) => {
    switch (patternValue) {
        case "PROBLEME TECHNIQUE":
            return t("Technical problem");
        case "RUPTURE DE STOCK":
            return t("Out of stock");
        case "FERMETURE ANNUELLE":
            return t("Annual closure");
        default:
            return patternValue || "";
    }
};
export function useTimeCalculations(
    selectedRows: any[],
    fuseau_horaire: string,
    localDuration: any
) {
    return useMemo(() => {
        const ordersTime = new Date(Date.parse(selectedRows[0]?.date));
        const currentTime = addHours(new Date(), offsetToHours(fuseau_horaire));
        const projectedEndTime = addMinutes(currentTime, localDuration.label);
        const endTime =
            localDuration.label !== undefined ? projectedEndTime : ordersTime;
        const timeDiff = dateDiff(currentTime, endTime);

        return {
            ordersTime,
            currentTime,
            projectedEndTime,
            endTime,
            timeDiff,
            hasExpired: timeDiff.sec < 0 || timeDiff.min < 0,
        };
    }, [selectedRows, fuseau_horaire, localDuration.label]);
}

export function useSuspensionMessage(
    isPauseOrders: boolean,
    fuseau_horaire: string,
    localDuration: any,
    localPattern: any,
    projectedEndTime: any
) {
    return useMemo(() => {
        if (!isPauseOrders) {
            return t("No interrupt commands are enabled");
        }

        const now = addHours(new Date(), offsetToHours(fuseau_horaire));

        if (localDuration.label === t("Undetermined")) {
            return `${t("Orders are suspended for an indefinite period")}, ${t(
                "Pattern"
            )}: ${localPattern.label || ""}`;
        }

        if (
            localDuration.label &&
            localDuration.label !== t("Undetermined") &&
            localDuration !== ""
        ) {
            if (projectedEndTime.toString() === "Invalid Date") {
                return `${t("Orders are suspended until")} ${t(
                    "Invalid date"
                )}, ${t("Pattern")}: ${localPattern.label || ""}`;
            }

            const suspensionDate = new Date(projectedEndTime);
            if (now < suspensionDate) {
                return `${t("Orders are suspended until")} ${moment(
                    suspensionDate
                ).format("DD-MM-YYYY HH:mm")}, ${t("Pattern")}: ${
                    localPattern.label || ""
                }`;
            }
        }

        return t("No interrupt commands are enabled");
    }, [
        isPauseOrders,
        fuseau_horaire,
        localDuration,
        localPattern,
        projectedEndTime,
    ]);
}
